import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Chip,
  IconButton,
  Checkbox,
  Toolbar,
  Typography,
  Card,
  CardContent,
  Grid,
  Tooltip,
  CircularProgress,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  GetApp as ExportIcon,
  CheckCircle as ActivateIcon,
  Cancel as DeactivateIcon,
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { RootState } from '../../store';
import {
  fetchInputs,
  fetchInputTypes,
  fetchInputsStatistics,
  deleteInput,
  setSearchTerm,
  setFilters,
  setPagination,
  setSelectedInput,
  setDialogOpen,
  setEditing,
  setSelectedInputIds,
  toggleInputSelection,
  selectAllInputs,
  deselectAllInputs,
  bulkActivateInputs,
  bulkDeactivateInputs,
} from '../../store/reducers/inputsSlice';
import { Input } from '../../services/inputsApi';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  searchBox: {
    marginBottom: theme.spacing(2),
  },
  filtersBox: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
  },
  statsGrid: {
    marginBottom: theme.spacing(3),
  },
  statCard: {
    textAlign: 'center',
  },
  statValue: {
    fontSize: '2rem',
    fontWeight: 'bold',
    color: theme.palette.primary.main,
  },
  statLabel: {
    color: theme.palette.text.secondary,
  },
  toolbar: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(1),
  },
  spacer: {
    flex: '1 1 100%',
  },
  actions: {
    color: theme.palette.text.secondary,
  },
  tableContainer: {
    marginTop: theme.spacing(2),
  },
  activeChip: {
    backgroundColor: theme.palette.success.main,
    color: theme.palette.success.contrastText,
  },
  inactiveChip: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: 200,
  },
}));

const InputsList: React.FC = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const state = useSelector((state: RootState) => state.inputs);
  const inputs: Input[] = Array.isArray(state?.inputs) ? state!.inputs : [];
  const inputTypes = Array.isArray(state?.inputTypes) ? state!.inputTypes : [];
  const loading = !!state?.loading;
  const error = state?.error || null;
  const searchTerm = state?.searchTerm || '';
  const filters = state?.filters || { active: 'all', inputTypeId: null };
  const pagination = state?.pagination || { page: 1, pageSize: 10 };
  const statistics = state?.statistics || { total: 0, active: 0, inactive: 0 };
  const selectedInputIds = Array.isArray(state?.selectedInputIds) ? state!.selectedInputIds : [];
  const bulkLoading = !!state?.bulkLoading;

  // Load data on component mount
  useEffect(() => {
    dispatch(fetchInputs());
    dispatch(fetchInputTypes());
    dispatch(fetchInputsStatistics());
  }, [dispatch]);

  // Filter and search inputs
  const filteredInputs = useMemo(() => {
    let filtered = inputs;

    // Apply search
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(input =>
        input.Name.toLowerCase().includes(searchLower) ||
        (input.InputType?.Name || '').toLowerCase().includes(searchLower)
      );
    }

    // Apply filters
    if (filters.active !== 'all') {
      filtered = filtered.filter(input => 
        input.Active === (filters.active === 'true')
      );
    }

    if (filters.inputTypeId) {
      filtered = filtered.filter(input => 
        input.InputTypeId === filters.inputTypeId
      );
    }

    return filtered;
  }, [inputs, searchTerm, filters]);

  // Pagination
  const paginatedInputs = useMemo(() => {
    const startIndex = Math.max(0, ((pagination?.page || 1) - 1) * (pagination?.pageSize || 10));
    return filteredInputs.slice(startIndex, startIndex + (pagination?.pageSize || 10));
  }, [filteredInputs, pagination?.page, pagination?.pageSize]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setSearchTerm(event.target.value));
  };

  const handleFilterChange = (filterName: string, value: any) => {
    dispatch(setFilters({ [filterName]: value }));
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    dispatch(setPagination({ page: newPage + 1 }));
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setPagination({ 
      pageSize: parseInt(event.target.value, 10),
      page: 1 
    }));
  };

  const handleAddInput = () => {
    dispatch(setSelectedInput(null));
    dispatch(setEditing(false));
    dispatch(setDialogOpen(true));
  };

  const handleEditInput = (input: Input) => {
    dispatch(setSelectedInput(input));
    dispatch(setEditing(true));
    dispatch(setDialogOpen(true));
  };

  const handleDeleteInput = (id: number) => {
    if (window.confirm('Are you sure you want to delete this input?')) {
      dispatch(deleteInput(id));
    }
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      dispatch(selectAllInputs());
    } else {
      dispatch(deselectAllInputs());
    }
  };

  const handleSelectInput = (inputId: number) => {
    dispatch(toggleInputSelection(inputId));
  };

  const isSelected = (inputId: number) => selectedInputIds.includes(inputId);
  const numSelected = selectedInputIds.length;

  if (loading && inputs.length === 0) {
    return (
      <Box className={classes.loadingContainer}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      {error && (
        <Alert severity="error" style={{ marginBottom: 16 }}>
          {error}
        </Alert>
      )}

      {/* Statistics Cards */}
      <Grid container spacing={3} className={classes.statsGrid}>
        <Grid item xs={12} sm={4}>
          <Card className={classes.statCard}>
            <CardContent>
              <Typography className={classes.statValue}>
                {statistics.total}
              </Typography>
              <Typography className={classes.statLabel}>
                Total Inputs
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card className={classes.statCard}>
            <CardContent>
              <Typography className={classes.statValue} style={{ color: '#4caf50' }}>
                {statistics.active}
              </Typography>
              <Typography className={classes.statLabel}>
                Active Inputs
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card className={classes.statCard}>
            <CardContent>
              <Typography className={classes.statValue} style={{ color: '#f44336' }}>
                {statistics.inactive}
              </Typography>
              <Typography className={classes.statLabel}>
                Inactive Inputs
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search */}
      <Box className={classes.searchBox}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search inputs by name or type..."
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: <SearchIcon color="action" />,
          }}
        />
      </Box>

      {/* Filters */}
      <Box className={classes.filtersBox}>
        <Grid container spacing={2} alignItems="center">
          <Grid item>
            <Typography variant="subtitle2">
              <FilterIcon fontSize="small" style={{ verticalAlign: 'middle', marginRight: 4 }} />
              Filters:
            </Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.active}
                onChange={(e) => handleFilterChange('active', e.target.value)}
                label="Status"
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="true">Active</MenuItem>
                <MenuItem value="false">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel>Input Type</InputLabel>
              <Select
                value={filters.inputTypeId || ''}
                onChange={(e) => handleFilterChange('inputTypeId', e.target.value || null)}
                label="Input Type"
              >
                <MenuItem value="">All Types</MenuItem>
                {inputTypes.map((type) => (
                  <MenuItem key={type.Id} value={type.Id}>
                    {type.Name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Box>

      {/* Toolbar */}
      <Toolbar className={classes.toolbar}>
        {numSelected > 0 ? (
          <Typography color="inherit" variant="subtitle1">
            {numSelected} selected
          </Typography>
        ) : (
          <Typography variant="h6">
            Inputs ({filteredInputs.length})
          </Typography>
        )}
        <div className={classes.spacer} />
        <div className={classes.actions}>
          {numSelected > 0 ? (
            <>
              <Tooltip title="Activate Selected">
                <IconButton 
                  onClick={() => dispatch(bulkActivateInputs(selectedInputIds))}
                  disabled={bulkLoading}
                >
                  <ActivateIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Deactivate Selected">
                <IconButton 
                  onClick={() => dispatch(bulkDeactivateInputs(selectedInputIds))}
                  disabled={bulkLoading}
                >
                  <DeactivateIcon />
                </IconButton>
              </Tooltip>
            </>
          ) : (
            <>
              <Tooltip title="Export">
                <IconButton>
                  <ExportIcon />
                </IconButton>
              </Tooltip>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleAddInput}
              >
                Add New Input
              </Button>
            </>
          )}
        </div>
      </Toolbar>

      {/* Table */}
      <TableContainer component={Paper} className={classes.tableContainer}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={numSelected > 0 && numSelected < filteredInputs.length}
                  checked={filteredInputs.length > 0 && numSelected === filteredInputs.length}
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedInputs.map((input) => {
              const isItemSelected = isSelected(input.Id);
              return (
                <TableRow
                  key={input.Id}
                  hover
                  selected={isItemSelected}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={isItemSelected}
                      onChange={() => handleSelectInput(input.Id)}
                    />
                  </TableCell>
                  <TableCell>{input.Name}</TableCell>
                  <TableCell>
                    {input.InputType?.Name || 'Unknown Type'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={input.Active ? 'Active' : 'Inactive'}
                      className={input.Active ? classes.activeChip : classes.inactiveChip}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Edit">
                      <IconButton 
                        size="small"
                        onClick={() => handleEditInput(input)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton 
                        size="small"
                        onClick={() => handleDeleteInput(input.Id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        component="div"
        count={filteredInputs.length}
        rowsPerPage={pagination.pageSize}
        page={pagination.page - 1}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
      />
    </Box>
  );
};

export default InputsList; 